import { RefAttributes, useRef } from 'react';
import {
  Image,
  OverlayTrigger,
  Stack,
  Tooltip,
  TooltipProps,
} from 'react-bootstrap';
import useAvatarImageFallback from '@hooks/useAvatarImageFallback';
import IMessageData from '@th-types/message.type';
import MessageBubble from '@components/Chat/ChatBody/MessageBubble/MessageBubble';

import './styles.css';

interface ChatMessageProps {
  authorName: string;
  message: IMessageData;
  avatarImageUrl: string;
  sentByMe: boolean;
  resendMessage: (message: IMessageData | undefined) => void;
  firstInBlock: boolean;
}

function ChatMessage({
  authorName,
  avatarImageUrl,
  firstInBlock,
  message,
  sentByMe,
  resendMessage,
}: ChatMessageProps) {
  const imageRef = useRef<HTMLImageElement>(null);
  // hook to use default avatar image if the src doesn't load
  useAvatarImageFallback(imageRef);

  const renderAuthorNameTooltip = (
    props: JSX.IntrinsicAttributes &
      TooltipProps &
      RefAttributes<HTMLDivElement>
  ) => (
    <Tooltip id="button-tooltip" {...props}>
      {authorName}
    </Tooltip>
  );

  return message ? (
    <Stack>
      <Stack
        direction="horizontal"
        gap={2}
        className={`align-items-start mb-3 ${sentByMe ? 'sent-by-me' : ''}`}
      >
        <div className="avatar-container">
          {firstInBlock ? (
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 250, hide: 400 }}
              overlay={renderAuthorNameTooltip}
            >
              <Image
                ref={imageRef}
                src={avatarImageUrl}
                alt="message-author-avatar"
                data-testid="message-author-avatar"
              />
            </OverlayTrigger>
          ) : null}
        </div>
        <div className="message-column">
          {firstInBlock && !sentByMe && authorName ? (
            <small className="text-muted author-name">{authorName}</small>
          ) : null}
          <MessageBubble
            messageContent={message.text}
            messageSent={message.sent}
            messageSending={message.sending}
            onResendMessage={() => resendMessage(message)}
            creationDate={message.created}
          />
        </div>
      </Stack>
    </Stack>
  ) : null;
}

export default ChatMessage;
